import React, { useEffect, useRef, useState } from 'react';
import { useTraduzioni } from '../../i18n';
import { apriPdf } from '../../utils/pdf';

// Una pagina di PDF disegnata da noi su una tela (pdf.js). Il perché sta in utils/pdf.js: dentro un <iframe> su
// iPhone non si vede niente, e «vai a pagina 2» ricaricava il documento intero.
//
//   <VisorePdf blob={blob} pagina={2} zoom={150} onPagine={(n) => …} titolo="Carta d'identità" />
//
// È **comandato da fuori**: pagina e ingrandimento li tiene chi lo usa, che ha già la sua barra (la stessa che
// comanda anche le immagini). `onPagine` dice quante pagine ha davvero il documento.
//
// Se pdf.js non ce la fa (file rotto, browser antico), si torna all'<iframe> di prima invece di mostrare il vuoto:
// un ripiego che mostra qualcosa è meglio di una pagina bianca.

const VisorePdf = ({ blob, url = null, pagina = 1, zoom = 100, onPagine = null, titolo = '' }) => {
  const { t } = useTraduzioni();
  const tela = useRef(null);
  const documento = useRef(null);
  const [stato, setStato] = useState('apro');   // 'apro' | 'pronto' | 'ripiego'
  const avvisaPagine = useRef(onPagine);
  avvisaPagine.current = onPagine;

  // Apertura: una volta per documento. `chiudi()` libera la memoria di pdf.js, che su un PDF di venti pagine non
  // è poca.
  useEffect(() => {
    let vivo = true;
    setStato('apro');
    documento.current = null;
    apriPdf(blob)
      .then((aperto) => {
        if (!vivo) { aperto.chiudi(); return; }
        documento.current = aperto;
        avvisaPagine.current?.(aperto.pagine);
        setStato('pronto');
      })
      .catch(() => { if (vivo) setStato('ripiego'); });
    return () => {
      vivo = false;
      documento.current?.chiudi();
      documento.current = null;
    };
  }, [blob]);

  // Disegno: a ogni cambio di pagina o ingrandimento. Niente ricarica del file.
  useEffect(() => {
    if (stato !== 'pronto' || !documento.current || !tela.current) return undefined;
    let vivo = true;
    // `disegna` è asincrono: due clic rapidi sulla freccia lanciavano due disegni sulla stessa tela e vinceva il
    // più lento. Il guardiano `vivo` fa buttare via quello vecchio.
    documento.current.disegna(tela.current, pagina, zoom / 100).catch(() => { if (vivo) setStato('ripiego'); });
    return () => { vivo = false; };
  }, [stato, pagina, zoom]);

  if (stato === 'ripiego') {
    return (
      <iframe src={`${url || ''}#page=${pagina}&toolbar=0&navpanes=0`} title={titolo}
        data-testid="visore-pdf-ripiego" />
    );
  }
  return (
    <>
      {stato === 'apro' && <p>{t('documenti.anteprima.apro')}</p>}
      <canvas ref={tela} className="visore-pdf" aria-label={titolo} role="img" data-testid="visore-pdf"
        style={stato === 'apro' ? { display: 'none' } : undefined} />
    </>
  );
};

export default VisorePdf;
